import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  styled,
  alpha,
  useTheme,
  TextField,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Autocomplete,
  MenuItem,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Collapse,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Card,
  CardContent,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import WorkOffIcon from "@mui/icons-material/WorkOff";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import VisibilityIcon from "@mui/icons-material/Visibility";
import BadgeIcon from "@mui/icons-material/Badge";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import CakeIcon from "@mui/icons-material/Cake";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import NotificationSnackbar from "./NotificationBar";

const ResponsiveTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: "600px",
  overflowX: "auto",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "12px",
  "&::-webkit-scrollbar": { height: "8px", width: "8px" },
  "&::-webkit-scrollbar-track": {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: alpha(theme.palette.primary.main, 0.25),
    borderRadius: "4px",
    "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.4) },
  },
}));

function Employees() {
  const theme = useTheme();
  const PRIMARY_COLOR = "#0a2e52";
  const SECONDARY_COLOR = "#1565C0";
  const DEFAULT_COLOR = "#FFFFFF";
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [employeeToAssign, setEmployeeToAssign] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [employeeToView, setEmployeeToView] = useState(null);
  const [entities, setEntities] = useState([]);
  const [loadingEntities, setLoadingEntities] = useState(false);
  const [planningEntities, setPlanningEntities] = useState([]);
  const [loadingPlanningEntities, setLoadingPlanningEntities] = useState(false);
  const [units, setUnits] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  const [employeeList, setEmployeeList] = useState([]);
  const [employeeLoading, setEmployeeLoading] = useState(false);
  const [employeeError, setEmployeeError] = useState(null);
  const [showEmployeeData, setShowEmployeeData] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedActionEmployee, setSelectedActionEmployee] = useState(null);
  const actionMenuOpen = Boolean(anchorEl);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const handleSnackbarClose = () => setSnackbarOpen(false);

  // Change Employee Group States
  const [changeGroupModalOpen, setChangeGroupModalOpen] = useState(false);
  const [employeeGroups, setEmployeeGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [employeeToChangeGroup, setEmployeeToChangeGroup] = useState(null);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    genderId: "",
    passportNumber: "",
    phoneNumber: "",
    email: "",
    entity: "",
    entitySector: "",
    planningEntity: "",
    unit: "",
    position: "",
    positionName: "",
    isSecondEmployment: 0,
  });

  const steps = ["Employee Details", "Assign Position"];
  const isStep0Valid = Boolean(
    formData.firstName?.trim() &&
    formData.lastName?.trim() &&
    formData.genderId &&
    formData.passportNumber?.trim() &&
    formData.phoneNumber?.trim() &&
    formData.email?.trim(),
  );
  const isStep1Valid = Boolean(formData.position);

  useEffect(() => {
    const fetchEntities = async () => {
      try {
        setLoadingEntities(true);
        const response = await fetch("/api/support/entities");
        const data = await response.json();
        setEntities(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching entities:", error);
        setEntities([]);
      } finally {
        setLoadingEntities(false);
      }
    };
    fetchEntities();
  }, []);

  useEffect(() => {
    const fetchEmployeeGroups = async () => {
      try {
        setLoadingGroups(true);
        const response = await fetch("/api/support/employee-groups");
        const data = await response.json();
        setEmployeeGroups(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching employee groups:", error);
        setEmployeeGroups([]);
      } finally {
        setLoadingGroups(false);
      }
    };
    fetchEmployeeGroups();
  }, []);

  useEffect(() => {
    const fetchPlanningEntities = async () => {
      if (!formData.entitySector) {
        setPlanningEntities([]);
        return;
      }
      try {
        setLoadingPlanningEntities(true);
        const response = await fetch(
          `/api/support/planning-entities?entitySectorId=${formData.entitySector}`,
        );
        const data = await response.json();
        setPlanningEntities(
          data.planningEntities && Array.isArray(data.planningEntities)
            ? data.planningEntities
            : [],
        );
      } catch (error) {
        console.error("Error fetching planning entities:", error);
        setPlanningEntities([]);
      } finally {
        setLoadingPlanningEntities(false);
      }
    };
    fetchPlanningEntities();
  }, [formData.entitySector]);

  useEffect(() => {
    const fetchUnitsAndPositions = async () => {
      if (!formData.planningEntity) {
        setUnits([]);
        return;
      }
      try {
        setLoadingUnits(true);
        const response = await fetch(
          `/api/support/planning-entity-positions?planningEntityId=${formData.planningEntity}`,
        );
        const data = await response.json();
        let fetchedUnits = [];
        if (Array.isArray(data) && data.length > 0) {
          const entitySectors = data[0].entitySectors || [];
          const planningEntitiesFallback = data[0].planningEntities || [];
          if (entitySectors.length > 0) {
            fetchedUnits = entitySectors[0].units || [];
          } else if (planningEntitiesFallback.length > 0) {
            fetchedUnits = planningEntitiesFallback[0].units || [];
          } else if (data[0].units) {
            fetchedUnits = data[0].units;
          }
        }
        setUnits(fetchedUnits);
      } catch (error) {
        console.error("Error fetching units and positions:", error);
        setUnits([]);
      } finally {
        setLoadingUnits(false);
      }
    };
    fetchUnitsAndPositions();
  }, [formData.planningEntity]);

  const handleEmployeeSearch = async () => {
    const term = employeeSearchTerm.trim();
    if (!term) {
      setShowEmployeeData(false);
      setEmployeeList([]);
      return;
    }
    try {
      setEmployeeLoading(true);
      setEmployeeError(null);
      const res = await fetch("/api/support/find-employee-from-entity-sector", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ entitySectorId: null, searchKeyword: term }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) {
        const msg =
          typeof json === "string"
            ? json
            : (json && (json.message || json.Message)) || `HTTP ${res.status}`;
        setEmployeeError(msg);
        setShowEmployeeData(true);
        setEmployeeList([]);
        return;
      }

      const list = Array.isArray(json)
        ? json
        : Array.isArray(json?.data)
          ? json.data
          : json
            ? [json]
            : [];
      if (list.length > 0) {
        setEmployeeList(list);
        setShowEmployeeData(true);
        setEmployeeError(null);
      } else {
        setEmployeeList([]);
        setShowEmployeeData(true);
        setEmployeeError("No record found");
      }
    } catch (err) {
      console.error("Employee search failed:", err);
      setEmployeeError("Search failed. Please try again.");
      setShowEmployeeData(true);
      setEmployeeList([]);
    } finally {
      setEmployeeLoading(false);
    }
  };

  const handleClearSearch = () => {
    setEmployeeSearchTerm("");
    setEmployeeError(null);
    setShowEmployeeData(false);
    setEmployeeList([]);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleOpenAddModal = () => {
    setFormData({
      firstName: "",
      lastName: "",
      genderId: "",
      passportNumber: "",
      phoneNumber: "",
      email: "",
      entity: "",
      entitySector: "",
      planningEntity: "",
      unit: "",
      position: "",
      positionName: "",
      isSecondEmployment: 0,
    });
    setActiveStep(0);
    setAddModalOpen(true);
  };

  const handleAddForeignSubmit = async () => {
    if (!formData.position) return;
    const payload = {
      passportNumber: formData.passportNumber,
      positionId: formData.position,
      phoneNumber: formData.phoneNumber,
      email: formData.email,
      firstname: formData.firstName,
      lastName: formData.lastName,
      isSecondEmployment: formData.isSecondEmployment,
      genderId: formData.genderId,
    };
    try {
      const response = await fetch("/api/support/add-foreign-staff", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setSnackbarMessage("Foreign staff added successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setAddModalOpen(false);
        setActiveStep(0);
      } else {
        const errorData = await response.json();
        setSnackbarMessage(errorData.message || "Failed to add foreign staff.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Error submitting:", error);
      setSnackbarMessage("An error occurred. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleMenuOpen = (event, employee) => {
    setAnchorEl(event.currentTarget);
    setSelectedActionEmployee(employee);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedActionEmployee(null);
  };

  const handleActionViewDetails = () => {
    if (selectedActionEmployee) {
      setEmployeeToView(selectedActionEmployee);
      setViewModalOpen(true);
    }
    handleMenuClose();
  };

  const handleActionAssignPosition = () => {
    if (selectedActionEmployee) {
      setEmployeeToAssign(selectedActionEmployee);
      setFormData((prev) => ({
        ...prev,
        entity: "",
        entitySector: "",
        planningEntity: "",
        unit: "",
        position: "",
        positionName: "",
        isSecondEmployment: 0,
      }));
      setAssignModalOpen(true);
    }
    handleMenuClose();
  };

  const handleActionRemoveActing = async () => {
    if (!selectedActionEmployee || !selectedActionEmployee.employeePositionId) {
      setSnackbarMessage("Missing Employee Position ID. Cannot remove acting.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      handleMenuClose();
      return;
    }

    const empPosId = Array.isArray(selectedActionEmployee.employeePositionId)
      ? selectedActionEmployee.employeePositionId[0]
      : selectedActionEmployee.employeePositionId;

    try {
      const response = await fetch("/api/support/remove-acting", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          employeePositionId: empPosId,
        }),
      });

      if (response.ok) {
        setSnackbarMessage("Acting status removed successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setEmployeeList((prevList) =>
          prevList.map((emp) =>
            emp.id === selectedActionEmployee.id
              ? { ...emp, isacting: false, ispayableActing: false }
              : emp,
          ),
        );
      } else {
        const errorData = await response.json().catch(() => ({}));
        setSnackbarMessage(errorData.message || "Failed to remove acting.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Error removing acting:", error);
      setSnackbarMessage("An error occurred. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
    handleMenuClose();
  };

  const handleActionChangeGroup = () => {
    if (selectedActionEmployee) {
      setEmployeeToChangeGroup(selectedActionEmployee);
      // Try to match the current employee group by name to prepopulate the select
      const matchedGroup = employeeGroups.find(
        (g) => g.employeeGroup === selectedActionEmployee.Employeegroup,
      );
      setSelectedGroupId(matchedGroup ? matchedGroup.id : "");
      setChangeGroupModalOpen(true);
    }
    handleMenuClose();
  };

  const handleChangeGroupSubmit = async () => {
    if (!employeeToChangeGroup || !selectedGroupId) return;

    const empPosId = Array.isArray(employeeToChangeGroup.employeePositionId)
      ? employeeToChangeGroup.employeePositionId[0]
      : employeeToChangeGroup.employeePositionId;

    if (!empPosId) {
      setSnackbarMessage("Missing Employee Position ID. Cannot change group.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    try {
      const response = await fetch("/api/support/update-employee-group", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          employeePositionId: empPosId,
          employeeGroupId: selectedGroupId,
        }),
      });

      if (response.ok) {
        setSnackbarMessage("Employee group updated successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setChangeGroupModalOpen(false);

        // Update local state to reflect the new group name
        const newGroupName =
          employeeGroups.find((g) => g.id === selectedGroupId)?.employeeGroup ||
          "";
        setEmployeeList((prevList) =>
          prevList.map((emp) =>
            emp.id === employeeToChangeGroup.id
              ? { ...emp, Employeegroup: newGroupName }
              : emp,
          ),
        );
      } else {
        const errorData = await response.json().catch(() => ({}));
        setSnackbarMessage(
          errorData.message || "Failed to update employee group.",
        );
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Error updating employee group:", error);
      setSnackbarMessage("An error occurred. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleAssignExistingSubmit = async () => {
    if (!formData.position) return;
    const payload = {
      employeeId: employeeToAssign.id,
      positionId: formData.position,
      isSecondEmployment: formData.isSecondEmployment,
    };
    try {
      const response = await fetch("/api/support/assign-foreign-staff", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setSnackbarMessage("Employee successfully assigned to position!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setAssignModalOpen(false);
      } else {
        const errorData = await response.json();
        setSnackbarMessage(errorData.message || "Failed to assign employee.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Error assigning:", error);
      setSnackbarMessage("An error occurred. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""}`.toUpperCase();
  };

  const DetailField = ({ label, value, icon: Icon }) => (
    <Box sx={{ display: "flex", alignItems: "flex-start", mb: 3 }}>
      {Icon && (
        <Box
          sx={{
            mr: 2,
            p: 1.2,
            borderRadius: 2,
            backgroundColor: alpha(SECONDARY_COLOR, 0.08),
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Icon fontSize="small" sx={{ color: SECONDARY_COLOR }} />
        </Box>
      )}
      <Box sx={{ flex: 1, overflow: "hidden" }}>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            display: "block",
            mb: 0.3,
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: PRIMARY_COLOR,
            wordWrap: "break-word",
            fontSize: "0.95rem",
          }}
        >
          {value || "N/A"}
        </Typography>
      </Box>
    </Box>
  );

  const renderAssignmentForm = () => {
    const selectedEntityObj = entities.find(
      (entity) => String(entity.id) === String(formData.entity),
    );
    const sectorOptions = selectedEntityObj?.entitySectors || [];
    const selectedUnitObj = units.find(
      (u) => String(u.unitId) === String(formData.unit),
    );
    const positionOptions = selectedUnitObj?.positions || [];
    const forceDropdownDownProps = {
      popper: {
        placement: "bottom-start",
        modifiers: [{ name: "flip", enabled: false }],
      },
    };

    return (
      <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
        {/* ENTITY */}
        <Grid item xs={12} sm={6}>
          <Autocomplete
            size="small"
            options={entities}
            loading={loadingEntities}
            componentsProps={forceDropdownDownProps}
            getOptionLabel={(option) => option.name || ""}
            value={selectedEntityObj || null}
            onChange={(event, newValue) => {
              setFormData((prev) => ({
                ...prev,
                entity: newValue ? newValue.id : "",
                entitySector: "",
                planningEntity: "",
                unit: "",
                position: "",
                positionName: "",
              }));
            }}
            isOptionEqualToValue={(option, value) =>
              String(option.id) === String(value.id)
            }
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: PRIMARY_COLOR }}
                  >
                    {option.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Entity ID: {option.id}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Entity"
                placeholder="Search Entity..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loadingEntities ? (
                        <CircularProgress color="inherit" size={18} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
        </Grid>

        {/* ENTITY SECTOR */}
        <Grid item xs={12} sm={6}>
          <Autocomplete
            size="small"
            disabled={!formData.entity}
            options={sectorOptions}
            componentsProps={forceDropdownDownProps}
            getOptionLabel={(option) =>
              selectedEntityObj
                ? `${selectedEntityObj.name} - ${option.sectorName}`
                : option.sectorName || ""
            }
            value={
              sectorOptions.find(
                (s) =>
                  String(s.entitySectorId) === String(formData.entitySector),
              ) || null
            }
            onChange={(event, newValue) => {
              setFormData((prev) => ({
                ...prev,
                entitySector: newValue ? newValue.entitySectorId : "",
                planningEntity: "",
                unit: "",
                position: "",
                positionName: "",
              }));
            }}
            isOptionEqualToValue={(option, value) =>
              String(option.entitySectorId) === String(value.entitySectorId)
            }
            renderOption={(props, option) => (
              <li {...props} key={option.entitySectorId}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: SECONDARY_COLOR, fontWeight: 600 }}
                  >
                    {selectedEntityObj?.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: PRIMARY_COLOR }}
                  >
                    {option.sectorName}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Entity Sector"
                placeholder={
                  formData.entity ? "Search Sector..." : "Select Entity First"
                }
              />
            )}
          />
        </Grid>

        {/* PLANNING ENTITY */}
        <Grid item xs={12} sm={6}>
          <Autocomplete
            size="small"
            disabled={!formData.entitySector || loadingPlanningEntities}
            options={planningEntities}
            componentsProps={forceDropdownDownProps}
            getOptionLabel={(option) =>
              option.name || option.planningEntityName || ""
            }
            value={
              planningEntities.find(
                (pe) =>
                  String(pe.id || pe.planningEntityId) ===
                  String(formData.planningEntity),
              ) || null
            }
            onChange={(event, newValue) => {
              setFormData((prev) => ({
                ...prev,
                planningEntity: newValue
                  ? newValue.id || newValue.planningEntityId
                  : "",
                unit: "",
                position: "",
                positionName: "",
              }));
            }}
            isOptionEqualToValue={(option, value) =>
              String(option.id || option.planningEntityId) ===
              String(value.id || value.planningEntityId)
            }
            renderOption={(props, option) => (
              <li {...props} key={option.id || option.planningEntityId}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: PRIMARY_COLOR }}
                >
                  {option.name || option.planningEntityName}
                </Typography>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Planning Entity"
                placeholder={
                  formData.entitySector
                    ? "Search Planning Entity..."
                    : "Select Sector First"
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loadingPlanningEntities ? (
                        <CircularProgress color="inherit" size={18} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
        </Grid>

        {/* UNIT */}
        <Grid item xs={12} sm={6}>
          <Autocomplete
            size="small"
            disabled={!formData.planningEntity || loadingUnits}
            options={units}
            componentsProps={forceDropdownDownProps}
            getOptionLabel={(option) => option.unitName || ""}
            value={
              units.find((u) => String(u.unitId) === String(formData.unit)) ||
              null
            }
            onChange={(event, newValue) => {
              setFormData((prev) => ({
                ...prev,
                unit: newValue ? newValue.unitId : "",
                position: "",
                positionName: "",
              }));
            }}
            isOptionEqualToValue={(option, value) =>
              String(option.unitId) === String(value.unitId)
            }
            renderOption={(props, option) => (
              <li {...props} key={option.unitId}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: PRIMARY_COLOR }}
                >
                  {option.unitName}
                </Typography>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Unit"
                placeholder={
                  formData.planningEntity
                    ? "Search Unit..."
                    : "Select Planning Entity First"
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loadingUnits ? (
                        <CircularProgress color="inherit" size={18} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
        </Grid>

        {/* POSITION */}
        <Grid item xs={12} sm={6}>
          <Autocomplete
            size="small"
            disabled={!formData.unit}
            options={positionOptions}
            componentsProps={forceDropdownDownProps}
            getOptionLabel={(option) => option.positionName || ""}
            value={
              positionOptions.find(
                (p) => String(p.positionId) === String(formData.position),
              ) || null
            }
            onChange={(event, newValue) => {
              if (newValue && newValue.numberOfVacantPosts === 0) {
                setSnackbarMessage("No vacant post to this position");
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
              }
              setFormData((prev) => ({
                ...prev,
                position: newValue ? newValue.positionId : "",
                positionName: newValue ? newValue.positionName : "",
              }));
            }}
            isOptionEqualToValue={(option, value) =>
              String(option.positionId) === String(value.positionId)
            }
            renderOption={(props, option) => (
              <li {...props} key={option.positionId}>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: PRIMARY_COLOR }}
                  >
                    {option.positionName}
                  </Typography>
                  {option.sourceOfFund && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        color: "text.secondary",
                        mt: 0.5,
                      }}
                    >
                      Fund: {option.sourceOfFund}
                    </Typography>
                  )}
                  {option.numberOfVacantPosts !== undefined && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        color:
                          option.numberOfVacantPosts === 0
                            ? "error.main"
                            : "text.secondary",
                        mt: 0.5,
                        fontWeight:
                          option.numberOfVacantPosts === 0 ? 600 : 400,
                      }}
                    >
                      Vacants: {option.numberOfVacantPosts}
                    </Typography>
                  )}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Position"
                placeholder={
                  formData.unit ? "Search Position..." : "Select Unit First"
                }
              />
            )}
          />
        </Grid>

        {/* IS SECOND EMPLOYMENT */}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.isSecondEmployment === 1}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    isSecondEmployment: e.target.checked ? 1 : 0,
                  }))
                }
                color="primary"
              />
            }
            label="Is Second Employment?"
          />
        </Grid>
      </Grid>
    );
  };

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: "16px",
        p: { xs: 2, sm: 3 },
        mb: 3,
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
        minHeight: "75vh",
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "stretch", md: "center" },
          mb: 4,
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, color: PRIMARY_COLOR }}>
          Employee Management
        </Typography>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAddModal}
          sx={{
            minHeight: 40,
            whiteSpace: "nowrap",
            px: 3,
            backgroundColor: SECONDARY_COLOR,
            "&:hover": { backgroundColor: PRIMARY_COLOR },
          }}
        >
          Add New Foreign Employee
        </Button>
      </Box>

      {/* --- SEARCH EXISTING EMPLOYEE (MAIN VIEW) --- */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 3,
          border: `1px solid ${alpha(PRIMARY_COLOR, 0.1)}`,
          bgcolor: alpha(PRIMARY_COLOR, 0.015),
        }}
      >
        <Typography
          variant="h6"
          sx={{ mb: 1, color: PRIMARY_COLOR, fontWeight: 600 }}
        >
          Search & Assign Existing Employee
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
          Find an employee in the system to assign them to a new position.
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
            mb: 3,
          }}
        >
          <TextField
            placeholder="Search by NID, Name, Email, or Phone..."
            size="small"
            value={employeeSearchTerm}
            onChange={(e) => {
              const v = e.target.value;
              setEmployeeSearchTerm(v);
              if (!v.trim()) setShowEmployeeData(false);
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {employeeSearchTerm && (
                    <IconButton
                      aria-label="clear"
                      size="small"
                      onClick={handleClearSearch}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
            sx={{
              flexGrow: 1,
              maxWidth: 500,
              bgcolor: "white",
              borderRadius: 1,
            }}
          />
          <Button
            variant="contained"
            disableElevation
            startIcon={
              employeeLoading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <SearchIcon />
              )
            }
            onClick={handleEmployeeSearch}
            disabled={employeeLoading}
            sx={{
              textTransform: "none",
              px: 4,
              py: 1,
              bgcolor: PRIMARY_COLOR,
              "&:hover": { bgcolor: SECONDARY_COLOR },
            }}
          >
            {employeeLoading ? "Searching..." : "Search"}
          </Button>
        </Box>

        <Collapse in={showEmployeeData} timeout="auto">
          {employeeError ? (
            <Typography
              color="error"
              sx={{ fontSize: "0.95rem", fontWeight: 500 }}
            >
              {employeeError}
            </Typography>
          ) : employeeList && employeeList.length > 0 ? (
            <Box>
              <Typography
                sx={{ fontWeight: 600, color: PRIMARY_COLOR, mb: 1.5 }}
              >
                Found {employeeList.length} Result
                {employeeList.length > 1 ? "s" : ""}
              </Typography>
              <ResponsiveTableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          bgcolor: alpha(PRIMARY_COLOR, 0.08),
                        }}
                      >
                        ID
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          bgcolor: alpha(PRIMARY_COLOR, 0.08),
                        }}
                      >
                        Name
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          bgcolor: alpha(PRIMARY_COLOR, 0.08),
                        }}
                      >
                        ID / Passport
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          bgcolor: alpha(PRIMARY_COLOR, 0.08),
                        }}
                      >
                        Institution
                      </TableCell>

                      <TableCell
                        sx={{
                          fontWeight: 700,
                          bgcolor: alpha(PRIMARY_COLOR, 0.08),
                        }}
                      >
                        Position
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          bgcolor: alpha(PRIMARY_COLOR, 0.08),
                        }}
                      >
                        Status
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          fontWeight: 700,
                          bgcolor: alpha(PRIMARY_COLOR, 0.08),
                        }}
                      >
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {employeeList.map((u) => (
                      <TableRow key={u.id} hover>
                        <TableCell sx={{ fontWeight: 600 }}>
                          #{u.id ?? "-"}
                        </TableCell>
                        <TableCell>
                          {`${u.lastName || ""} ${u.firstName || ""}`.trim() ||
                            "-"}
                        </TableCell>
                        <TableCell>{u.idNumber || "-"}</TableCell>
                        <TableCell>{u.entityName || "-"}</TableCell>
                        <TableCell>{u.positionName || "-"}</TableCell>
                        <TableCell>
                          <Chip
                            label={
                              u.isAvailable ? "In Service" : "Not in Service"
                            }
                            size="small"
                            sx={{
                              backgroundColor: u.isAvailable
                                ? alpha(theme.palette.success.main, 0.15)
                                : alpha(theme.palette.error.main, 0.15),
                              color: u.isAvailable
                                ? theme.palette.success.dark
                                : theme.palette.error.dark,
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            onClick={(e) => handleMenuOpen(e, u)}
                            size="small"
                            sx={{
                              color: PRIMARY_COLOR,
                              backgroundColor: alpha(PRIMARY_COLOR, 0.05),
                              "&:hover": {
                                backgroundColor: alpha(PRIMARY_COLOR, 0.15),
                              },
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ResponsiveTableContainer>

              <Menu
                anchorEl={anchorEl}
                open={actionMenuOpen}
                onClose={handleMenuClose}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    overflow: "visible",
                    filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.15))",
                    mt: 1,
                    minWidth: 220,
                    borderRadius: 2,
                    "& .MuiMenuItem-root": {
                      px: 2,
                      py: 1.2,
                      "&:hover": {
                        backgroundColor: alpha(PRIMARY_COLOR, 0.04),
                      },
                    },
                    "& .Mui-disabled": {
                      opacity: 0.5,
                    },
                  },
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              >
                <MenuItem onClick={handleActionViewDetails}>
                  <ListItemIcon>
                    <VisibilityIcon
                      fontSize="small"
                      sx={{ color: SECONDARY_COLOR }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary="View Details"
                    primaryTypographyProps={{
                      fontWeight: 500,
                      fontSize: "0.9rem",
                    }}
                  />
                </MenuItem>
                <MenuItem onClick={handleActionAssignPosition}>
                  <ListItemIcon>
                    <AssignmentIndIcon
                      fontSize="small"
                      sx={{ color: SECONDARY_COLOR }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary="Assign Position"
                    primaryTypographyProps={{
                      fontWeight: 500,
                      fontSize: "0.9rem",
                    }}
                  />
                </MenuItem>
                <MenuItem
                  onClick={handleActionRemoveActing}
                  disabled={
                    !(
                      selectedActionEmployee?.isacting ||
                      selectedActionEmployee?.isPayableActing
                    )
                  }
                >
                  <ListItemIcon>
                    <WorkOffIcon
                      fontSize="small"
                      sx={{
                        color: !(
                          selectedActionEmployee?.isacting ||
                          selectedActionEmployee?.ispayableActing
                        )
                          ? "inherit"
                          : theme.palette.error.main,
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary="Remove Acting"
                    primaryTypographyProps={{
                      fontWeight: 500,
                      fontSize: "0.9rem",
                    }}
                  />
                </MenuItem>
                <MenuItem onClick={handleActionChangeGroup}>
                  <ListItemIcon>
                    <PeopleAltIcon
                      fontSize="small"
                      sx={{ color: theme.palette.warning.main }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary="Change Employee Group"
                    primaryTypographyProps={{
                      fontWeight: 500,
                      fontSize: "0.9rem",
                    }}
                  />
                </MenuItem>
              </Menu>
            </Box>
          ) : (
            <Typography sx={{ color: "text.secondary" }}>
              No results found.
            </Typography>
          )}
        </Collapse>
      </Paper>
      <Dialog
        open={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        maxWidth="lg"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "16px",
            backgroundColor: "#f8fafc",
          },
          backgroundColor: "rgba(0, 0, 0, 0.6)",
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
            backgroundColor: "#ffffff",
            borderBottom: `1px solid ${alpha(PRIMARY_COLOR, 0.08)}`,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 800, color: PRIMARY_COLOR }}
          >
            Employee Profile
          </Typography>
          <IconButton
            onClick={() => setViewModalOpen(false)}
            size="small"
            sx={{
              color: "text.secondary",
              "&:hover": {
                color: PRIMARY_COLOR,
                bgcolor: alpha(PRIMARY_COLOR, 0.1),
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, md: 4 } }}>
          {employeeToView && (
            <Box>
              {/* Profile Header Widget */}
              <Paper
                elevation={0}
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "flex-start", sm: "center" },
                  p: 3,
                  mb: 4,
                  borderRadius: 4,
                  border: `1px solid ${alpha(SECONDARY_COLOR, 0.15)}`,
                  background: `linear-gradient(135deg, ${DEFAULT_COLOR} 0%, ${alpha(
                    SECONDARY_COLOR,
                    0.05,
                  )} 100%)`,
                }}
              >
                <Avatar
                  sx={{
                    width: 86,
                    height: 86,
                    bgcolor: PRIMARY_COLOR,
                    fontSize: "2.2rem",
                    fontWeight: 700,
                    mb: { xs: 2, sm: 0 },
                    mr: { sm: 3 },
                    boxShadow: `0 4px 14px ${alpha(PRIMARY_COLOR, 0.3)}`,
                  }}
                >
                  {getInitials(
                    employeeToView.firstName,
                    employeeToView.lastName,
                  )}
                </Avatar>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 800, color: PRIMARY_COLOR, mb: 0.5 }}
                  >
                    {`${employeeToView.firstName || ""} ${
                      employeeToView.lastName || ""
                    }`.trim()}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{ color: SECONDARY_COLOR, fontWeight: 600, mb: 1.5 }}
                  >
                    {employeeToView.positionName || "Position Not Assigned"}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                    <Chip
                      icon={<VerifiedUserIcon fontSize="small" />}
                      label={
                        employeeToView.isAvailable
                          ? "Active & In Service"
                          : "Not in Service"
                      }
                      color={employeeToView.isAvailable ? "success" : "default"}
                      sx={{ fontWeight: 600, borderRadius: 2 }}
                    />
                    {employeeToView.Employeegroup && (
                      <Chip
                        label={employeeToView.Employeegroup}
                        variant="outlined"
                        color="primary"
                        sx={{
                          fontWeight: 600,
                          borderRadius: 2,
                          bgcolor: "white",
                        }}
                      />
                    )}
                    {(employeeToView.isacting ||
                      employeeToView.ispayableActing) && (
                      <Chip
                        label="Acting"
                        color="warning"
                        size="small"
                        sx={{
                          fontWeight: 600,
                          borderRadius: 2,
                          alignSelf: "center",
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </Paper>

              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 4,
                      border: `1px solid ${alpha(PRIMARY_COLOR, 0.08)}`,
                      height: "100%",
                      boxShadow: "0px 4px 20px rgba(0,0,0,0.02)",
                    }}
                  >
                    <CardContent sx={{ p: 3, pt: 4 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 3 }}
                      >
                        <PersonOutlineIcon
                          sx={{ color: SECONDARY_COLOR, mr: 1 }}
                        />
                        <Typography
                          variant="h6"
                          sx={{ color: PRIMARY_COLOR, fontWeight: 700 }}
                        >
                          Personal Information
                        </Typography>
                      </Box>
                      <Divider
                        sx={{ mb: 3, borderColor: alpha(PRIMARY_COLOR, 0.05) }}
                      />
                      <DetailField
                        icon={BadgeIcon}
                        label="Employee-Id"
                        value={employeeToView.id}
                      />
                      <DetailField
                        icon={BadgeIcon}
                        label="NID / Passport Number"
                        value={employeeToView.idNumber}
                      />
                      <DetailField
                        icon={BadgeIcon}
                        label="Employee-Name"
                        value={`${employeeToView.firstName || ""} ${
                          employeeToView.lastName || ""
                        }`.trim()}
                      />
                      <DetailField
                        icon={EmailIcon}
                        label="Email Address"
                        value={employeeToView.email}
                      />
                      <DetailField
                        icon={PhoneIcon}
                        label="Phone Number"
                        value={employeeToView.phoneNumber}
                      />
                      <DetailField
                        icon={CakeIcon}
                        label="Date of Birth"
                        value={
                          employeeToView.dateOfBirth
                            ? new Date(
                                employeeToView.dateOfBirth,
                              ).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              })
                            : ""
                        }
                      />
                    </CardContent>
                  </Card>
                </Grid>

                {/* Employment Information Card */}
                <Grid item xs={12} md={6}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 4,
                      border: `1px solid ${alpha(PRIMARY_COLOR, 0.08)}`,
                      height: "100%",
                      boxShadow: "0px 4px 20px rgba(0,0,0,0.02)",
                    }}
                  >
                    <CardContent sx={{ p: 3, pt: 4 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 3 }}
                      >
                        <BusinessCenterIcon
                          sx={{ color: SECONDARY_COLOR, mr: 1 }}
                        />
                        <Typography
                          variant="h6"
                          sx={{ color: PRIMARY_COLOR, fontWeight: 700 }}
                        >
                          Employment Details
                        </Typography>
                      </Box>
                      <Divider
                        sx={{ mb: 3, borderColor: alpha(PRIMARY_COLOR, 0.05) }}
                      />

                      <DetailField
                        icon={CorporateFareIcon}
                        label="Institution / Entity"
                        value={employeeToView.entityName}
                      />
                      <DetailField
                        icon={BusinessCenterIcon}
                        label="Position Name"
                        value={employeeToView.positionName}
                      />
                      <DetailField
                        icon={PeopleAltIcon}
                        label="Employee Group"
                        value={employeeToView.Employeegroup}
                      />
                      <DetailField
                        icon={CalendarMonthIcon}
                        label="Employment Date"
                        value={
                          employeeToView.startDate
                            ? new Date(
                                employeeToView.startDate,
                              ).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              })
                            : ""
                        }
                      />
                      <DetailField
                        icon={CalendarMonthIcon}
                        label="End Date"
                        value={
                          employeeToView.endDate
                            ? new Date(
                                employeeToView.endDate,
                              ).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              })
                            : ""
                        }
                      />
                      <DetailField
                        icon={AssignmentIndIcon}
                        label="Acting Title Status"
                        value={
                          employeeToView.isacting
                            ? "Employee is currently Acting"
                            : "Not Acting"
                        }
                      />
                      <DetailField
                        icon={AssignmentIndIcon}
                        label="Is Payable Acting"
                        value={employeeToView.isPayableActing ? "Yes" : "No"}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, backgroundColor: "#ffffff" }}>
          <Button
            onClick={() => setViewModalOpen(false)}
            variant="contained"
            disableElevation
            sx={{
              fontWeight: 600,
              px: 4,
              py: 1,
              borderRadius: 2,
              backgroundColor: PRIMARY_COLOR,
              "&:hover": { backgroundColor: SECONDARY_COLOR },
            }}
          >
            Close Details
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        maxWidth="md"
        fullWidth
        sx={{
          "& .MuiDialog-paper": { borderRadius: "16px" },
          backgroundColor: "rgba(0, 0, 0, 0.7)",
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
            backgroundColor: theme.palette.primary.main,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: DEFAULT_COLOR }}
          >
            Assign Position - {employeeToAssign?.firstName}{" "}
            {employeeToAssign?.lastName}
          </Typography>
          <IconButton
            onClick={() => setAssignModalOpen(false)}
            size="small"
            sx={{ color: DEFAULT_COLOR }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {renderAssignmentForm()}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, px: 3, justifyContent: "space-between" }}>
          <Button
            onClick={() => setAssignModalOpen(false)}
            color="inherit"
            sx={{ fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAssignExistingSubmit}
            disabled={!isStep1Valid}
            sx={{
              ...(isStep1Valid && { backgroundColor: PRIMARY_COLOR }),
              px: 3,
              py: 1,
            }}
          >
            Complete Assignment
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        maxWidth="md"
        fullWidth
        sx={{
          "& .MuiDialog-paper": { borderRadius: "16px" },
          backgroundColor: "rgba(0, 0, 0, 0.7)",
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
            backgroundColor: theme.palette.primary.main,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: DEFAULT_COLOR }}
          >
            {activeStep === 0
              ? "Add New Foreign Employee"
              : "Assign Employee to Position"}
          </Typography>
          <IconButton
            onClick={() => setAddModalOpen(false)}
            size="small"
            sx={{ color: DEFAULT_COLOR }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 3 }}>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel
                  sx={{
                    "& .MuiStepIcon-root.Mui-active": { color: PRIMARY_COLOR },
                    "& .MuiStepIcon-root.Mui-completed": {
                      color: SECONDARY_COLOR,
                    },
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box sx={{ px: { xs: 0, sm: 2 } }}>
            {activeStep === 0 && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="First Name"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Last Name"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    size="small"
                    label="Gender"
                    name="genderId"
                    value={formData.genderId}
                    onChange={handleChange}
                  >
                    <MenuItem value="F">Female</MenuItem>
                    <MenuItem value="M">Male</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Passport Number"
                    name="passportNumber"
                    value={formData.passportNumber}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Phone Number"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Email Address"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
            )}
            {activeStep === 1 && renderAssignmentForm()}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, px: 3, justifyContent: "space-between" }}>
          <Button
            onClick={
              activeStep === 0
                ? () => setAddModalOpen(false)
                : () => setActiveStep(0)
            }
            color="inherit"
            sx={{ fontWeight: 600 }}
          >
            {activeStep === 0 ? "Cancel" : "Back"}
          </Button>

          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleAddForeignSubmit}
              disabled={!isStep1Valid}
              sx={{
                ...(isStep1Valid && { backgroundColor: PRIMARY_COLOR }),
                px: 3,
                py: 1,
              }}
            >
              Complete & Assign
            </Button>
          ) : (
            <Button
              variant="contained"
              disabled={!isStep0Valid}
              onClick={() => setActiveStep(1)}
              sx={{
                ...(isStep0Valid && { backgroundColor: SECONDARY_COLOR }),
                px: 3,
                py: 1,
              }}
            >
              Proceed to Assignment
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <Dialog
        open={changeGroupModalOpen}
        onClose={() => setChangeGroupModalOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiDialog-paper": { borderRadius: "16px" },
          backgroundColor: "rgba(0, 0, 0, 0.7)",
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
            backgroundColor: theme.palette.primary.main,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: DEFAULT_COLOR }}
          >
            Change Employee Group
          </Typography>
          <IconButton
            onClick={() => setChangeGroupModalOpen(false)}
            size="small"
            sx={{ color: DEFAULT_COLOR }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Typography variant="body2" sx={{ mb: 3 }}>
            Select a new employee group for{" "}
            <strong>
              {employeeToChangeGroup?.firstName}{" "}
              {employeeToChangeGroup?.lastName}
            </strong>
            .
          </Typography>
          <TextField
            fullWidth
            select
            size="small"
            label="Employee Group"
            value={selectedGroupId}
            onChange={(e) => setSelectedGroupId(e.target.value)}
            disabled={loadingGroups}
          >
            {employeeGroups.map((group) => (
              <MenuItem key={group.id} value={group.id}>
                {group.employeeGroup}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, px: 3, justifyContent: "space-between" }}>
          <Button
            onClick={() => setChangeGroupModalOpen(false)}
            color="inherit"
            sx={{ fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleChangeGroupSubmit}
            disabled={!selectedGroupId}
            sx={{ px: 3, py: 1, backgroundColor: PRIMARY_COLOR }}
          >
            Update Group
          </Button>
        </DialogActions>
      </Dialog>

      <NotificationSnackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Box>
  );
}

export default Employees;
